import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {addItemToCart, clearCart, deleteItemFromCart, fetchCartItems} from '../../http/cartAPI';
import {CART_ROUTE, ORDER_PLACING_ROUTE, SHOP_ROUTE} from '../../util/Constants';
import {Context} from '../../index';
import {observer} from 'mobx-react-lite';
import {addToGuestCart, clearGuestCart, readGuestCart} from '../../util/guestCart';
import {effectiveStockCap} from '../../util/cartLineAvailable';
import {Link} from 'react-router-dom';

function TrashIcon({className}) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
        >
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
    );
}

/**
 * @param {'page' | 'modal'} mode
 * @param {() => void} [onClose] — для модалки
 */
const CartPanel = observer(({mode = 'page', onClose}) => {
    const {user, cart} = useContext(Context);
    const [cartItems, setCartItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const isModal = mode === 'modal';

    const loadCart = useCallback(() => {
        setError('');
        if (user.isAuth) {
            fetchCartItems()
                .then((data) => {
                    setCartItems(data);
                    cart.setTotalFromLines(data);
                })
                .catch((err) => setError(err.message))
                .finally(() => setIsLoading(false));
        } else {
            const lines = readGuestCart();
            setCartItems(lines);
            setIsLoading(false);
            cart.setTotalFromLines(lines);
        }
    }, [user.isAuth, cart]);

    useEffect(() => {
        setIsLoading(true);
        loadCart();
    }, [loadCart]);

    const loadCartRef = useRef(loadCart);
    loadCartRef.current = loadCart;

    /** Синхронизация с оформлением заказа / другой вкладкой при изменении корзины. */
    useEffect(() => {
        const onSync = () => {
            loadCartRef.current();
        };
        window.addEventListener('lutica-cart-changed', onSync);
        return () => window.removeEventListener('lutica-cart-changed', onSync);
    }, []);

    const notifyCartChanged = () => {
        window.dispatchEvent(new CustomEvent('lutica-cart-changed'));
    };

    const lineKey = (item) => (user.isAuth ? item.id : item.itemId);

    const handleIncrease = (item) => {
        if (user.isAuth) {
            addItemToCart(item.id)
                .then(() => {
                    notifyCartChanged();
                })
                .catch((err) => setError(err.response?.data?.message || err.message));
        } else {
            addToGuestCart(
                {id: item.itemId, title: item.title, price: item.price, count: item.count},
                1,
            );
            notifyCartChanged();
        }
    };

    const handleDecrease = (item) => {
        if (user.isAuth) {
            deleteItemFromCart(item.id)
                .then(() => {
                    notifyCartChanged();
                })
                .catch((err) => setError(err.response?.data?.message || err.message));
        } else {
            addToGuestCart(
                {id: item.itemId, title: item.title, price: item.price, count: item.count},
                -1,
            );
            notifyCartChanged();
        }
    };

    const handleClearCart = () => {
        if (user.isAuth) {
            clearCart()
                .then(() => {
                    setCartItems([]);
                    cart.setTotalFromLines([]);
                    notifyCartChanged();
                })
                .catch((err) => setError(err.response?.data?.message || err.message));
        } else {
            clearGuestCart();
            setCartItems([]);
            cart.setTotalFromLines([]);
            notifyCartChanged();
        }
    };

    const calculateTotal = () => {
        return cartItems.reduce((sum, item) => {
            return sum + parseFloat(item.price) * item.itemCount;
        }, 0).toFixed(2);
    };

    const goCheckout = () => {
        if (onClose) onClose();
        navigate(ORDER_PLACING_ROUTE);
    };

    if (isLoading) {
        return (
            <div
                className={`flex flex-col items-center justify-center gap-3 ${isModal ? 'min-h-[14rem]' : 'min-h-[40vh]'}`}
                aria-busy="true"
                aria-label="Загрузка корзины"
            >
                <div
                    className="h-10 w-10 animate-spin rounded-full border-2 border-stone-200 border-t-pine-700"
                    role="status"
                />
            </div>
        );
    }

    const inner = (
        <>
            <div className="flex items-start justify-between gap-2">
                <h2
                    id={isModal ? 'cart-modal-title' : undefined}
                    className="min-w-0 text-xl font-semibold tracking-tight text-bark-900"
                >
                    Корзина
                    <span className="ml-2 inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-pine-100 px-2 text-sm font-medium text-pine-900">
                        {cartItems.reduce((s, i) => s + Number(i.itemCount || 0), 0)}
                    </span>
                </h2>
                <div className="flex shrink-0 items-center gap-0.5">
                    {cartItems.length > 0 ? (
                        <button
                            type="button"
                            onClick={handleClearCart}
                            className="touch-manipulation rounded-full p-2 text-bark-500 hover:bg-red-50 hover:text-red-700"
                            aria-label="Очистить корзину"
                            title="Очистить корзину"
                        >
                            <TrashIcon className="block" />
                        </button>
                    ) : null}
                    {isModal ? (
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full p-2 text-bark-500 hover:bg-cream-100"
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                    ) : null}
                </div>
            </div>

            {error ? (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {error}
                </div>
            ) : null}

            {cartItems.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-stone-200/70 bg-white/90 px-4 py-5 text-center shadow-soft backdrop-blur-sm">
                    <p className="font-medium text-bark-800">Корзина пуста</p>
                    <p className="mt-2 text-sm text-bark-500">Добавьте товары из каталога</p>
                    {isModal ? (
                        <button type="button" onClick={onClose} className="lut-btn-pine mt-4 text-sm">
                            Продолжить покупки
                        </button>
                    ) : (
                        <Link to={SHOP_ROUTE} className="lut-btn-pine mt-4 inline-flex justify-center text-sm">
                            Перейти в каталог
                        </Link>
                    )}
                </div>
            ) : (
                <>
                    <ul className="mt-3 divide-y divide-stone-100 rounded-2xl border border-stone-200/70 bg-white">
                        {cartItems.map((item) => {
                            const lineMax = effectiveStockCap(item.count, item);
                            return (
                                <li key={lineKey(item)} className="px-2.5 py-2.5 sm:px-3 sm:py-3">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="min-w-0 flex-1 self-start pt-0.5">
                                            <h3 className="line-clamp-2 text-sm font-medium leading-snug text-bark-900">
                                                {item.title}
                                            </h3>
                                            <p className="mt-0.5 text-xs tabular-nums text-bark-500">
                                                {item.price} ₽ × {item.itemCount}
                                            </p>
                                            {item.itemCount >= lineMax ? (
                                                <p className="mt-0.5 text-[11px] leading-tight text-red-600">
                                                    Макс. на складе
                                                </p>
                                            ) : null}
                                        </div>
                                        <div className="flex shrink-0 items-center gap-0.5">
                                            <button
                                                type="button"
                                                onClick={() => handleDecrease(item)}
                                                className="flex h-8 w-8 touch-manipulation items-center justify-center rounded-full border border-stone-200 text-base leading-none text-bark-800 hover:bg-cream-100"
                                            >
                                                −
                                            </button>
                                            <span className="min-w-[1.5rem] text-center text-sm font-medium tabular-nums">
                                                {item.itemCount}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleIncrease(item)}
                                                disabled={item.itemCount >= lineMax}
                                                className="flex h-8 w-8 touch-manipulation items-center justify-center rounded-full border border-stone-200 text-base leading-none text-bark-800 hover:bg-cream-100 disabled:opacity-40"
                                            >
                                                +
                                            </button>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-1">
                                            <span className="text-sm font-semibold tabular-nums text-bark-900">
                                                {(parseFloat(item.price) * item.itemCount).toFixed(2)} ₽
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleDecrease(item)}
                                                className="touch-manipulation rounded-full p-1.5 text-lg leading-none text-red-600 hover:bg-red-50"
                                                aria-label="Удалить позицию"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="mt-4 flex flex-col gap-3 border-t border-stone-100 pt-4">
                        <p className="text-base text-bark-800">
                            Итого: <strong className="text-pine-900">{calculateTotal()} ₽</strong>
                        </p>
                        <div className={isModal ? '' : 'max-w-xs'}>
                            <button
                                type="button"
                                onClick={goCheckout}
                                className={`lut-btn-mustard py-3 text-base font-semibold shadow-md touch-manipulation ${
                                    isModal ? 'w-full' : 'w-full sm:w-auto sm:min-w-[12rem]'
                                }`}
                            >
                                Оформить заказ
                            </button>
                        </div>
                    </div>
                    {isModal ? (
                        <p className="mt-3 text-center text-xs text-bark-500">
                            <Link to={CART_ROUTE} onClick={onClose} className="font-medium text-pine-700 hover:underline">
                                Открыть страницу корзины
                            </Link>
                        </p>
                    ) : null}
                </>
            )}
        </>
    );

    return <div>{inner}</div>;
});

export default CartPanel;
